// Every knob in the app lives in one flat object keyed 'group.name', is
// described by SCHEMA, and round-trips through the URL hash. A URL is
// therefore a complete configuration: a lecture slide can link to "the pads
// at 30px", and the study hands out condition URLs.

import { SOUNDS, PARAM_RANGES, PRESETS } from './engine/presets.js'

const num = (def, min, max, step = 1) => ({ type: 'number', def, min, max, step })
const bool = (def) => ({ type: 'boolean', def })
const pick = (def, options) => ({ type: 'enum', def, options })

export const SCHEMA = {
  // Shell state, not a knob: it rides the hash so a slide can link to one
  // interface, but the panel skips it.
  ui: {
    interface: { type: 'string', def: '', label: 'interface', internal: true },
  },
  pads: {
    count: { ...num(4, 2, 16), label: 'pad count' },
    size: { ...num(96, 24, 220), label: 'pad size (px)' },
    gap: { ...num(12, 0, 80), label: 'gap (px)' },
    labels: { ...bool(true), label: 'show labels' },
    layout: { ...pick('grid', ['grid', 'row']), label: 'layout' },
  },
  cli: {
    autocomplete: { ...bool(true), label: 'autocomplete' },
    verbosity: { ...pick('helpful', ['terse', 'helpful']), label: 'error verbosity' },
    history: { ...bool(true), label: 'history' },
    hiddenLabels: { ...bool(false), label: 'hide sound names (recall)' },
  },
  camera: {
    divisions: { ...num(2, 2, 4), label: 'grid divisions' },
    smoothing: { ...num(0.35, 0, 0.95, 0.05), label: 'smoothing' },
    mirror: { ...bool(true), label: 'mirror' },
    dwellMs: { ...num(120, 0, 800, 10), label: 'dwell (ms)' },
    showOverlay: { ...bool(true), label: 'show tracking overlay' },
    // The continuous x/y -> setParam layer. Latent in v1, per the spec, and
    // off by default so it cannot quietly affect a study round.
    continuous: { ...bool(false), label: 'continuous x/y mapping' },
  },
  engine: {
    tempo: { ...num(100, 40, 240), label: 'tempo (bpm)' },
    volume: { ...num(0.8, 0, 1, 0.01), label: 'master volume' },
  },
  study: {
    logging: { ...bool(false), label: 'logging on' },
    participantId: { type: 'string', def: '', label: 'participant id' },
    condition: { type: 'string', def: '', label: 'condition' },
    trials: { ...num(12, 3, 60), label: 'trials per round' },
    cueIntervalMs: { ...num(1800, 600, 5000, 50), label: 'cue interval (ms)' },
    cueType: { ...pick('both', ['visual', 'audio', 'both']), label: 'cue type' },
  },
}

// The FM presets are parameters too: 'preset.kick.modulationIndex' etc.
for (const sound of SOUNDS) {
  SCHEMA[`preset.${sound}`] = Object.fromEntries(
    Object.entries(PARAM_RANGES).map(([name, range]) => [
      name,
      { type: 'number', def: PRESETS[sound][name], ...range, label: name },
    ]),
  )
}

export function defaults() {
  const out = {}
  for (const [group, fields] of Object.entries(SCHEMA)) {
    for (const [name, spec] of Object.entries(fields)) out[`${group}.${name}`] = spec.def
  }
  return out
}

export function specFor(key) {
  const i = key.lastIndexOf('.')
  return SCHEMA[key.slice(0, i)]?.[key.slice(i + 1)]
}

function coerce(key, raw) {
  const spec = specFor(key)
  if (!spec) return undefined
  switch (spec.type) {
    case 'number': {
      const v = Number(raw)
      if (!Number.isFinite(v)) return undefined
      return Math.max(spec.min, Math.min(spec.max, v))
    }
    case 'boolean':
      return raw === true || raw === 'true' || raw === '1'
    case 'enum':
      return spec.options.includes(raw) ? raw : undefined
    default:
      return String(raw)
  }
}

export function createParams() {
  const values = { ...defaults(), ...fromHash() }
  const listeners = new Set()
  let writeScheduled = false

  function scheduleHashWrite() {
    if (writeScheduled) return
    writeScheduled = true
    queueMicrotask(() => {
      writeScheduled = false
      const hash = toHash(values)
      history.replaceState(null, '', hash ? `#${hash}` : location.pathname + location.search)
    })
  }

  return {
    get: (key) => values[key],
    all: () => ({ ...values }),

    group(name) {
      const out = {}
      for (const field of Object.keys(SCHEMA[name] ?? {})) out[field] = values[`${name}.${field}`]
      return out
    },

    set(key, raw) {
      const v = coerce(key, raw)
      if (v === undefined || values[key] === v) return values[key]
      values[key] = v
      scheduleHashWrite()
      for (const fn of listeners) fn(key, v)
      return v
    },

    setMany(obj) {
      for (const [k, v] of Object.entries(obj)) this.set(k, v)
    },

    reset() {
      this.setMany(defaults())
    },

    // fn(key, value); returns an unsubscribe
    onChange(fn) { listeners.add(fn); return () => listeners.delete(fn) },

    // Subscribe to one group only, the common case for an interface module.
    onGroupChange(group, fn) {
      return this.onChange((key) => { if (key.startsWith(`${group}.`)) fn(this.group(group)) })
    },

    // Deliberately excludes PRIVATE_KEYS -- this is the link that gets pasted.
    shareUrl() {
      const hash = toHash(values)
      return `${location.origin}${location.pathname}${hash ? `#${hash}` : ''}`
    },
  }
}

// Keys that must never travel in a shared URL. The participant id is
// generated into the params at startup, so without this every "copy config
// URL" would paste someone's id onto a lecture slide.
const PRIVATE_KEYS = new Set(['study.participantId'])

// Only non-default values go in the hash, so a shared URL stays readable.
export function toHash(values, { includePrivate = false } = {}) {
  const d = defaults()
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(values)) {
    if (!includePrivate && PRIVATE_KEYS.has(k)) continue
    if (v !== d[k] && v !== '' && v != null) p.set(k, String(v))
  }
  return p.toString()
}

export function fromHash(hash = location.hash) {
  const out = {}
  if (!hash || hash.length < 2) return out
  for (const [k, raw] of new URLSearchParams(hash.slice(1))) {
    const v = coerce(k, raw)
    if (v !== undefined) out[k] = v
  }
  return out
}
